import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ToggleButton, ToggleButtonGroup } from '@cast/design-system';

import { WORKLOAD_CATEGORIES } from '../_constants';
import type { WorkloadCategory } from '../_constants';
import {
  buildInventoryWorkloadViewPath,
  buildInventoryWorkloadsViewPath,
} from '../navigation';
import {
  getLiveSearchParams,
  getWorkloadsCategoryFromPathname,
  getWorkloadsViewFromPathname,
} from '../_utils';
import { carryInventoryListFiltersAcrossCategories } from '../_utils/persistInventoryListFilters';

type WorkloadCategoryOption = {
  value: WorkloadCategory;
  label: string;
};

const WORKLOAD_CATEGORY_OPTIONS: WorkloadCategoryOption[] = [
  { value: WORKLOAD_CATEGORIES.DEFAULT, label: 'Default' },
  { value: WORKLOAD_CATEGORIES.AUTOSCALING, label: 'Autoscaling' },
  { value: WORKLOAD_CATEGORIES.SAVINGS, label: 'Savings' },
  { value: WORKLOAD_CATEGORIES.COMPUTE, label: 'Compute' },
  { value: WORKLOAD_CATEGORIES.RELIABILITY, label: 'Reliability' },
  { value: WORKLOAD_CATEGORIES.NETWORK, label: 'Network' },
  { value: WORKLOAD_CATEGORIES.GPU_UTILIZATION, label: 'GPU utilization' },
];

type InventoryWorkloadsCategoryToggleArgs = {
  hideDefault?: boolean;
  hiddenCategories?: WorkloadCategory[];
};

export const InventoryWorkloadsCategoryToggle = ({
  hideDefault = false,
  hiddenCategories = [],
}: InventoryWorkloadsCategoryToggleArgs) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { clusterId, workloadId } = useParams<{
    clusterId?: string;
    workloadId?: string;
  }>();

  const category = getWorkloadsCategoryFromPathname(pathname);
  const view = getWorkloadsViewFromPathname(pathname);

  const options = WORKLOAD_CATEGORY_OPTIONS.filter(({ value }) => {
    if (hideDefault && value === WORKLOAD_CATEGORIES.DEFAULT) {
      return false;
    }

    return !hiddenCategories.includes(value);
  });

  const buildPath = (nextCategory: WorkloadCategory) => {
    if (!clusterId) return pathname;

    if (workloadId) {
      return buildInventoryWorkloadViewPath({
        clusterId,
        workloadId,
        category: nextCategory,
        view,
      });
    }

    return buildInventoryWorkloadsViewPath({
      clusterId,
      category: nextCategory,
      view,
    });
  };

  return (
    <ToggleButtonGroup
      size="sm"
      value={category || options[0]?.value}
      onChange={(_, value) => {
        if (!value) return;

        const nextSearch = carryInventoryListFiltersAcrossCategories(
          category,
          value,
          getLiveSearchParams()
        );

        navigate(
          { pathname: buildPath(value), search: nextSearch.toString() },
          { replace: true }
        );
      }}
    >
      {options.map(({ value, label }) => (
        <ToggleButton
          key={value}
          value={value}
          text={label}
          testId={`workloads-category-${value}`}
        />
      ))}
    </ToggleButtonGroup>
  );
};
